import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Pagination } from "@mui/material";
import DataTable from "../../common/DataTable";
import { FormData } from "../../types/FormData";
import { useNavigate } from "react-router-dom";
import { fetchCustomers } from "../../api/customerAPI";
import CustomDateRangePicker from "../../pages/selller/dashboards/components/custom-date-range";
import SortBy from "../../common/filters/SortBy";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import colors from "../../theme/Colours";
import { getCustomersServiceExciseAPI } from "../../api/getCustomersAPI";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Toast from "../../common/Toast";

import {
  containerStyle,
  searchAndSortContainer,
  mainContentStyle,
  paginationContainerStyle,
  paginationCaptionStyle,
  checkDetailsStyles,
  addCustomerButtonStyle,
  SortBYContainerStyle,
  customersNameStyle,
  grandTotalAmountStyle,
  downloadSelectStyle,
  downloadMenuPropsStyle,
} from "./DisplayCustomers.styles";
import moment from "moment";
import SearchBar from "../../common/filters/SearchBar";
import { exportToExcel } from "../../utils/exportToExcel";
import { exportToCSV } from "../../utils/exportToCSV";
import { useOutsideClose } from "../../hooks/useOutsideClose";

type SortTypes = "newest" | "oldest";

const DOWNLOAD_OPTIONS = [
  { value: "customers_excel", label: "Customers (Excel)" },
  { value: "customers_csv", label: "Customers (CSV)" },
] as const;

type DownloadType = "" | (typeof DOWNLOAD_OPTIONS)[number]["value"];

const DisplayCustomers = () => {
  const [customers, setCustomers] = useState<FormData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const options = ["Newest", "Oldest"];
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<SortTypes>("newest");
  const [toast, setToast] = useState({
    show: false,
    message: "",
    error: false,
  });

  const showToast = (message: string, error = false) => {
    setToast({ show: true, message, error });
  };

  const [downloadType, setDownloadType] = useState<DownloadType>("");

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
    totalPages: 0,
  });

  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);

  const [openFilter, setOpenFilter] = React.useState<"sort" | "date" | null>(
    null
  );
  const filterContainerRef = React.useRef<HTMLDivElement>(null);

  useOutsideClose(filterContainerRef, openFilter !== null, () =>
    setOpenFilter(null)
  );

  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId");

  const columns = [
    { label: "Id", field: "clientCode" },
    { label: "Name", field: "name" },
    { label: "Created Date", field: "createdAt" },
    { label: "Email", field: "email" },
    { label: "Sales Rep", field: "salesRep" },
    { label: "Ledger", field: "ledger" },
    { label: "Outstanding", field: "outstanding" },
  ];

  useEffect(() => {
    if (!sortBy) {
      setSortOrder("newest");
      return;
    }
    setSortOrder(sortBy.toLowerCase() === "oldest" ? "oldest" : "newest");
  }, [sortBy]);

  const calculateOutstanding = (customer: any): number => {
    if (!customer || customer.totalDebitAmount === undefined) {
      return 0;
    }

    return Number(customer.totalDebitAmount);
  };

  const fetchCustomerData = useCallback(
    async (download: string, search?: string, page?: number) => {
      const apiSortOrder = sortOrder === "newest" ? "DESC" : "ASC";

      const adjustedFromDate = from
        ? moment(from).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
        : undefined;

      const adjustedToDate = to
        ? moment(to).endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
        : undefined;

      return await fetchCustomers({
        page: download === "true" ? undefined : page,
        limit: download === "true" ? undefined : pagination.rowsPerPage,
        search: search?.toLowerCase(),
        startDate: adjustedFromDate,
        endDate: adjustedToDate,
        download,
        sortOrder: apiSortOrder,
      });
    },
    [sortOrder, from, to, pagination.rowsPerPage]
  );

  const generateCustomersExcel = useCallback(async () => {
    try {
      const response = await fetchCustomerData("true", searchTerm);

      if (!response?.data) {
        showToast("No data received from API", true);
        return;
      }

      // Handle the actual API response structure from get-customers endpoint
      const apiData = response.data.data || response.data;
      const allCustomers = Array.isArray(apiData)
        ? apiData
        : apiData.customers || [];

      const excelData = allCustomers.map((customer: any) => {
        const outstanding = calculateOutstanding(customer);
        const absValue = Math.abs(outstanding).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return {
          "Customer Code": customer.clientCode ?? "-",
          Name: customer.outletName,
          "Created Date": moment(customer.createdAt).format("DD MMM YY"),
          Email: customer.emailId,
          SalesRep: customer.assignedSalesRep?.salesRep?.name || "N/A",
          Outstanding:
            outstanding > 0
              ? `'(${absValue})`
              : outstanding < 0
                ? `-${absValue}`
                : absValue,
        };
      });

      await exportToExcel(excelData, "customers.xlsx");
    } catch (error) {
      console.error("Error generating Customers Excel:", error);
      showToast("Error generating Customers Excel", true);
    }
  }, [fetchCustomerData, searchTerm]);

  const generateCustomersCSV = useCallback(async () => {
    try {
      const response = await fetchCustomerData("true", searchTerm);

      if (!response?.data) {
        showToast("No data received from API", true);
        return;
      }

      // Handle the actual API response structure from get-customers endpoint
      const apiData = response.data.data || response.data;
      const allCustomers = Array.isArray(apiData)
        ? apiData
        : apiData.customers || [];

      const csvData = allCustomers.map((customer: any) => {
        const outstanding = calculateOutstanding(customer);
        const absValue = Math.abs(outstanding).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return {
          "Customer Code": customer.clientCode ?? "-",
          Name: customer.outletName,
          "Created Date": moment(customer.createdAt).format("DD MMM YY"),
          Email: customer.emailId,
          SalesRep: customer.assignedSalesRep?.salesRep?.name || "N/A",
          Outstanding:
            outstanding > 0
              ? `(${absValue})`
              : outstanding < 0
                ? `-${absValue}`
                : absValue,
        };
      });

      await exportToCSV(csvData, "customers.csv");
    } catch (error) {
      console.error("Error generating Customers CSV:", error);
      showToast("Error generating Customers CSV", true);
    }
  }, [fetchCustomerData, searchTerm]);

  const generateServiceExciseExcel = useCallback(async () => {
    try {
      const list = await getCustomersServiceExciseAPI(searchTerm);

      await exportToExcel(
        list.map((c) => ({
          "Customer Code": c.clientCode ?? "-",
          "Outlet name": c.outletName,
          "Company GST Number": c.companyGSTNumber,
          "Company PAN Number": c.companyPANNumber,
          "License Name": c.licenseName,
          "License GST Number": c.licenseGSTNumber,
          "License PAN Number": c.licensePANNumber,
        })),
        "service_excise_data.xlsx"
      );
    } catch (error) {
      console.error("Error generating Service & Excise Excel:", error);
      showToast("Error generating Service & Excise Excel", true);
    }
  }, [searchTerm]);

  const handleDownloadSelect = useCallback(
    async (val: string) => {
      const selected = val as Exclude<DownloadType, "">;
      setDownloadType(selected);

      try {
        if (selected === "customers_excel") {
          await generateCustomersExcel();
        } else if (selected === "customers_csv") {
          await generateCustomersCSV();
        } else if (selected === "serviceExcise") {
          await generateServiceExciseExcel();
        }
      } finally {
        setDownloadType("");
      }
    },
    [generateCustomersExcel, generateCustomersCSV, generateServiceExciseExcel]
  );

  const loadCustomers = useCallback(
    async (page: number, search?: string) => {
      try {
        console.log("loadCustomers called with:", { page, search });

        const response = await fetchCustomerData("false", search, page);

        console.log("API response:", response);

        if (response.data) {
          // Handle API response structure
          const apiData = response.data.data || response.data;

          console.log("Parsed API data:", apiData);

          const customersArray = Array.isArray(apiData)
            ? apiData
            : apiData.customers || [];
          const totalRecords = apiData.totalRecords || 0;
          const totalPages = apiData.totalPages || 0;

          console.log("Customers array:", customersArray);

          // EMPTY STATE
          if (!customersArray.length) {
            console.log("No customers found in API response");

            setCustomers([]);

            setPagination({
              page: 1,
              rowsPerPage: pagination.rowsPerPage,
              totalRows: 0,
              totalPages: 0,
            });

            return;
          }

          // NORMAL FLOW
          setPagination({
            page: page,
            rowsPerPage: pagination.rowsPerPage,
            totalRows: totalRecords,
            totalPages: totalPages,
          });

          const customerData = customersArray.map((customer: any) => {
            console.log("Processing customer:", customer);

            return {
              ...customer,
              clientCode: customer.clientCode || "",
              firstName: customer.outletName
                ? customer.outletName.split(" ")[0]
                : "",
              lastName: customer.outletName
                ? customer.outletName.split(" ").slice(1).join(" ")
                : "",
              email: customer.emailId || "",
              createdAt: customer.createdAt
                ? moment(customer.createdAt).format("DD MMM YY")
                : "",
              salesRep: customer.assignedSalesRep?.salesRep?.name || "N/A",
              outstanding: calculateOutstanding(customer),
            };
          });

          console.log("Processed customer data:", customerData);

          setCustomers(customerData);
        } else {
          console.log("No response.data found");

          setCustomers([]);

          setPagination({
            page: 1,
            rowsPerPage: pagination.rowsPerPage,
            totalRows: 0,
            totalPages: 0,
          });
        }
      } catch (error) {
        console.error("Error fetching customers:", error);

        setCustomers([]);

        setPagination({
          page: 1,
          rowsPerPage: pagination.rowsPerPage,
          totalRows: 0,
          totalPages: 0,
        });
      }
    },
    [fetchCustomerData, pagination.rowsPerPage]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 1500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
    loadCustomers(1, debouncedSearchTerm);
  }, [sortOrder, debouncedSearchTerm, loadCustomers]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
    loadCustomers(1, debouncedSearchTerm);
  }, [from, to, debouncedSearchTerm, loadCustomers]);
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadCustomers(newPage, debouncedSearchTerm);
  };

  const redirectToLedger = (customerId: string) => {
    navigate(`/maltora-seller/ledgers/${customerId}`);
  };

  const redirectToUser = (customer: any) => {
    navigate(`/maltora-seller/customer/${customer.id}`);
  };

  const { page, rowsPerPage, totalPages, totalRows } = pagination;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + customers.length;
  const emptyRows = rowsPerPage - customers.slice(startIndex, endIndex).length;

  console.log("Rendering - customers state:", customers);
  console.log("Rendering - customers length:", customers.length);

  const tableData = customers.map((customer) => {
    const raw = customer.outstanding ?? 0;
    const displayValue = Math.abs(raw);
    return {
      clientCode: customer.clientCode ? `${customer.clientCode}` : "-",
      name: (
        <Typography component="span" sx={customersNameStyle}>
          {`${customer.firstName} ${customer.lastName}`}
        </Typography>
      ),
      createdAt: customer.createdAt,
      email: customer.email,
      salesRep: customer.salesRep || "N/A",
      ledger: (
        <button
          onClick={(e) => {
            e.stopPropagation();
            redirectToLedger(customer.id);
          }}
          style={checkDetailsStyles}
        >
          Check Details
        </button>
      ),
      outstanding: (
        <Typography component="span" sx={grandTotalAmountStyle(false)}>
          {displayValue}
        </Typography>
      ),
      onClick: () => redirectToUser(customer),
    };
  });

  return (
    <>
      <Box sx={containerStyle}>
        {/* Search and Sort */}
        <Box sx={searchAndSortContainer}>
          {/* Search bar */}
          <Box sx={{ flex: 1 }}>
            <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
          </Box>
          <Box
            ref={filterContainerRef}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: openFilter === "date" ? 998 : undefined,
            }}
          >
            {/* Add new customer button */}
            <button
              onClick={() => navigate(`/maltora-seller/create-new-customer`)}
              style={addCustomerButtonStyle as React.CSSProperties}
            >
              + Add new Customer
            </button>

            {/* Sort By Dropdown */}
            <SortBy
              containerSx={SortBYContainerStyle}
              options={options}
              selected={sortBy}
              setSelected={setSortBy}
              variant="seller"
              hideOptions
              isOpen={openFilter === "sort"}
              onToggle={(open) => setOpenFilter(open ? "sort" : null)}
            />

            {/* DateRange */}
            <CustomDateRangePicker
              fromDate={from}
              toDate={to}
              setFromDate={setFrom}
              setToDate={setTo}
              placeholder="Select date range"
              disableFuture
              showReset
              sx={{
                height: "38px",
              }}
            />

            {/* Download Dropdown */}
            <Select
              value=""
              onChange={(e: SelectChangeEvent) =>
                handleDownloadSelect(e.target.value)
              }
              displayEmpty
              renderValue={() => "Download"}
              IconComponent={KeyboardArrowDownIcon}
              sx={downloadSelectStyle}
              MenuProps={downloadMenuPropsStyle}
            >
              {DOWNLOAD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {/* DataTable */}
        <Box sx={{ ...mainContentStyle, overflowX: "auto" }}>
          <Box sx={{ minWidth: "1185px" }}>
            <DataTable
              columns={columns.map((col) => ({
                ...col,
                label: (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span>{col.label}</span>
                    <ArrowDownwardIcon
                      fontSize="small"
                      sx={{ color: colors.customerTablelabel }}
                    />
                  </span>
                ),
                align: (col.field === "ledger" || col.field === "outstanding"
                  ? "center"
                  : "left") as "center" | "left",
                labelStyle: {
                  fontSize: "14px",
                  fontWeight: 500,
                  color: colors.customerTablelabel,
                  backgroundColor: colors.customerTableHeaderBG,
                  "&.MuiTableCell-root.MuiTableCell-root": {
                    padding: "12px 13px",
                    width: col.field === "ledger" ? "30%" : "21%",
                  },
                },
                cellStyle: {
                  fontSize: "14px",
                  "&.MuiTableCell-root.MuiTableCell-root": {
                    padding: col.field === "ledger" ? "12px 0px" : "12px 13px",
                  },
                  color:
                    col.field !== "name"
                      ? colors.customerTablelabel
                      : undefined,
                },
              }))}
              data={tableData}
              emptyStateMessage="No data available"
              emptyRows={emptyRows}
              onRowClick={(row) => row.onClick()}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        {customers.length > 0 && (
          <Box>
            {/* Caption and Pagination */}
            <Box sx={paginationContainerStyle}>
              <Typography variant="subtitle2" sx={paginationCaptionStyle}>
                Showing data {startIndex + 1} to {startIndex + customers.length}{" "}
                of {totalRows} entries
              </Typography>

              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => handleChangePage(newPage)}
                shape="rounded"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
      </Box>
      <Toast
        showToast={toast.show}
        setShowToast={(val) => setToast((prev) => ({ ...prev, show: val }))}
        toastMessage={toast.message}
        error={toast.error}
      />
    </>
  );
};

export default DisplayCustomers;
